import { PropsWithChildren, useCallback, useMemo } from 'react';

import * as yup from 'yup';

import {
  Button,
  DestructiveDialogResult,
  Divider,
  Icons,
  Tooltip,
  notify,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import {
  Webhook,
  SlackConfiguration,
  CreateWebhook,
  DeliveryMethod,
} from '@cast/types';
import { makeValidateSync, getDocsLink } from '@cast/utils';

import { RadioOptionCard } from 'components/inputs';
import {
  useCreateWebhookMutation,
  useDeleteSlackWorkspaceMutation,
  useInstallSlackWorkspace,
  useTestSlackConfiguration,
  useTestSlackWorkspace,
} from 'hooks/mutations/notifications';
import { useGetSlackWorkspaces } from 'hooks/queries/notifications';

import { useDisconnectSlackWorkspaceDialog } from '../_hooks';
import { AuthenticationKeysRow } from './AuthenticationKeysRow';
import { CategorySelect } from './CategorySelect';
import { ClustersSection } from './ClustersSection';
import { SeverityCheckboxes } from './NotificationSeverity';
import { OperationSelect } from './OperationSelect';
import { RequestTemplateRow } from './RequestTemplateRow';
import { SlackIntegrationBlock } from './SlackIntegrationBlock';
import { FormModel, SlackIntegrationStatus } from './types';
import { makeApiModel, makeFormModel } from './utils';

import 'core/extensions/yup';

import {
  FormMode,
  RffForm,
  RffInput,
  RffRadioGroup,
  WhenMode,
} from '@cast/shared-rff-ui';

type GroupTitleProps = PropsWithChildren<{ optional?: boolean }>;

const GroupTitle = ({ optional, children }: GroupTitleProps) => {
  return (
    <Typography
      variant="P14B"
      color={tokens.theme.fg.structural.primary}
      display="inline-flex"
      pb={4}
    >
      {children}
      {optional && (
        <Typography
          fontStyle="italic"
          color={tokens.theme.fg.structural.secondary}
          fontWeight={400}
          variant="inherit"
          pl="4px"
        >
          Optional
        </Typography>
      )}
    </Typography>
  );
};

type GroupProps = PropsWithChildren<{ title: string }>;

const Group = ({ title, children }: GroupProps) => {
  return (
    <Stack pt="24px">
      <GroupTitle>{title}</GroupTitle>
      <Divider sx={{ mb: 16 }} />
      {children}
    </Stack>
  );
};

const validator = makeValidateSync(
  yup.object().shape({
    name: yup.string().required('Name is required'),
    deliveryMethod: yup.string().required('Delivery method is required'),
    callbackUrl: yup.string().when('deliveryMethod', {
      is: DeliveryMethod.Webhook,
      then: (schema) => schema.required('Callback URL is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    slackChannels: yup.array().when('deliveryMethod', {
      is: DeliveryMethod.Slack,
      then: (schema) =>
        schema
          .of(yup.string())
          .min(1, 'Please add at least one Slack channel ID')
          .max(5, 'You can add up to 5 Slack channel IDs'),
      otherwise: (schema) => schema.notRequired(),
    }),
    selectedSeverities: yup
      .array()
      .of(yup.string())
      .min(1, 'Please select at least one severity'),
    requestTemplate: yup.string().when('deliveryMethod', {
      is: DeliveryMethod.Webhook,
      then: (schema) => schema.required('Request template is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    subcategory: yup.string().when('category', {
      is: (category: string) => !!category && category !== 'all',
      then: (schema) => schema.required('Operation is required'),
    }),
  })
);

type Props = {
  webhook?: Webhook;
  slackConfiguration?: SlackConfiguration;
  mode: FormMode;
  onSubmit?: (formModel: FormModel) => void;
  onDirty?: (dirty: boolean) => void;
  onCancel?: () => void;
  restoredFormState?: FormModel;
  defaultName?: string;
};

export const AlertForm = ({
  mode,
  webhook,
  slackConfiguration,
  onSubmit,
  onDirty,
  onCancel,
  restoredFormState,
  defaultName,
}: Props) => {
  const alertConfig = webhook || slackConfiguration;
  const { data: slackWorkspacesData } = useGetSlackWorkspaces();
  const { mutate: installSlackWorkspace } = useInstallSlackWorkspace();
  const { mutate: deleteSlackWorkspace } = useDeleteSlackWorkspaceMutation();
  const { openDialog: openDisconnectDialog } =
    useDisconnectSlackWorkspaceDialog();
  const { mutate: testSlackConfiguration, isPending: isTestingSlack } =
    useTestSlackConfiguration();
  const { mutate: testSlackWorkspace, isPending: isTestingWorkspace } =
    useTestSlackWorkspace();
  const { mutate: testWebhook, isPending: isTestingWebhook } =
    useCreateWebhookMutation();

  const slackIntegrationStatus: SlackIntegrationStatus = useMemo(() => {
    const workspaces = slackWorkspacesData?.items || [];
    if (workspaces.length === 0) {
      return 'not_connected';
    }

    const firstWorkspace = workspaces[0];

    // If installation is completed, status is connected
    if (firstWorkspace.installCompletedAt) {
      return 'connected';
    }

    // If installation expired, status is not_connected
    if (firstWorkspace.installExpiresAt) {
      const expiresAt = new Date(firstWorkspace.installExpiresAt);
      if (expiresAt < new Date()) {
        return 'not_connected';
      }
    }

    // If not completed and not expired, status is pending
    return 'pending';
  }, [slackWorkspacesData]);

  const workspaceId = slackWorkspacesData?.items?.[0]?.id;

  const initialValues = useMemo(() => {
    const formModel = makeFormModel(alertConfig);
    const baseValues =
      restoredFormState ||
      (!alertConfig && defaultName
        ? { ...formModel, name: defaultName }
        : formModel);

    // If creating a new slack configuration and we have a workspace, set the workspaceId
    if (!alertConfig && workspaceId && slackIntegrationStatus === 'connected') {
      return {
        ...baseValues,
        slackWorkspaceId: workspaceId,
        slackIntegrationStatus,
      };
    }

    return {
      ...baseValues,
      slackIntegrationStatus,
    };
  }, [
    restoredFormState,
    alertConfig,
    defaultName,
    workspaceId,
    slackIntegrationStatus,
  ]);

  const handleTest = useCallback(
    (formValues: FormModel) => {
      if (formValues.deliveryMethod === DeliveryMethod.Webhook) {
        if (!formValues.callbackUrl || !formValues.requestTemplate) {
          return;
        }
        testWebhook({
          ...makeApiModel(formValues),
          dryRun: true,
        } as CreateWebhook);
        return;
      }

      // If configuration is already saved, test the saved configuration
      if (slackConfiguration?.id) {
        testSlackConfiguration(slackConfiguration.id);
        return;
      }

      // If testing before save, test the workspace with current form values
      if (!workspaceId) {
        notify.error('Please connect a Slack workspace first');
        return;
      }

      if (!formValues.slackChannels || formValues.slackChannels.length === 0) {
        notify.error('Please select at least one Slack channel');
        return;
      }

      testSlackWorkspace({
        workspaceId,
        name: formValues.name,
        channels: formValues.slackChannels,
      });
    },
    [
      testSlackConfiguration,
      testSlackWorkspace,
      testWebhook,
      slackConfiguration?.id,
      workspaceId,
    ]
  );

  return (
    <RffForm
      mode={mode}
      initialValues={initialValues}
      initialValuesEqual={(a, b) => a?.id === b?.id}
      onSubmit={onSubmit}
      onDirty={onDirty}
      validate={validator}
      array
      testId="alert-form"
      sx={{ display: 'flex', flex: 1 }}
    >
      {({ values }) => (
        <Stack flex={1} minWidth={0}>
          <Group title="Delivery method">
            <RffRadioGroup
              name="deliveryMethod"
              row
              sx={{ gap: 16, width: '100%' }}
            >
              <RadioOptionCard
                testId="delivery-method-slack"
                value={DeliveryMethod.Slack}
                isSelected={values.deliveryMethod === DeliveryMethod.Slack}
                label="Slack"
                icon={null}
                typographyVariant="P12M"
              />
              <RadioOptionCard
                testId="delivery-method-webhook"
                value={DeliveryMethod.Webhook}
                isSelected={values.deliveryMethod === DeliveryMethod.Webhook}
                label="Webhook"
                icon={null}
                typographyVariant="P12M"
              />
            </RffRadioGroup>
            {values.deliveryMethod === DeliveryMethod.Slack ? (
              <Stack gap="16px">
                <SlackIntegrationBlock
                  status={slackIntegrationStatus}
                  onConnect={() => {
                    installSlackWorkspace({
                      formState: values,
                      mode,
                      currentPath:
                        window.location.pathname + window.location.search,
                      alertId: values.id,
                    });
                  }}
                  onRetry={() => {
                    installSlackWorkspace({
                      formState: values,
                      mode,
                      currentPath:
                        window.location.pathname + window.location.search,
                      alertId: values.id,
                    });
                  }}
                  workspaceData={slackWorkspacesData?.items?.[0]}
                  onDisconnect={() => {
                    const workspace = slackWorkspacesData?.items?.[0];
                    const workspaceId = workspace?.id;
                    const workspaceName = workspace?.name;
                    if (workspaceId && workspaceName) {
                      openDisconnectDialog(
                        {
                          workspace: { id: workspaceId, name: workspaceName },
                        },
                        {
                          onClose: (result) => {
                            if (result === DestructiveDialogResult.CONFIRMED) {
                              deleteSlackWorkspace(workspaceId);
                            }
                          },
                        }
                      );
                    }
                  }}
                />
              </Stack>
            ) : null}
          </Group>
          <Group title="Details">
            <Stack gap="16px">
              <RffInput
                name="name"
                label="Name"
                placeholder="Enter name"
                testId="alert-name-input"
              />
              <CategorySelect />

              <OperationSelect />
              {values.deliveryMethod === DeliveryMethod.Webhook ? (
                <RffInput
                  name="callbackUrl"
                  label="Callback URL"
                  placeholder="Enter URL"
                  testId="alert-callback-url-input"
                />
              ) : null}
            </Stack>
          </Group>
          <Group title="Clusters">
            <ClustersSection />
          </Group>
          <Group title="Notification severity">
            <SeverityCheckboxes />
          </Group>
          {values.deliveryMethod === DeliveryMethod.Webhook ? (
            <Group title="Template">
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="P12M" mb={8}>
                  Request template
                </Typography>
                <ExternalLink
                  link={getDocsLink('docs_webhook_examples')}
                  sx={{ typography: 'P12M' }}
                >
                  Documentation
                </ExternalLink>
              </Stack>
              <RequestTemplateRow />
            </Group>
          ) : null}
          {values.deliveryMethod === DeliveryMethod.Webhook ? (
            <Group title="Authorization keys">
              <Stack pb="24px">
                <AuthenticationKeysRow />
              </Stack>
            </Group>
          ) : null}
          <Stack
            mt="auto"
            direction="row"
            justifyContent="space-between"
            py={16}
          >
            {/* Left side: Test button for Slack */}
            {values.deliveryMethod === DeliveryMethod.Slack && (
              <Button
                variant="ghost"
                size="md"
                onClick={() => handleTest(values)}
                disabled={
                  slackIntegrationStatus !== 'connected' ||
                  isTestingSlack ||
                  isTestingWorkspace ||
                  !values.slackChannels?.length
                }
                loading={isTestingSlack || isTestingWorkspace}
                startIcon={<Icons.PaperPlaneTilt />}
                testId="alert-test-slack-button"
              >
                Test
              </Button>
            )}

            {/* Left side: Test button for Webhook */}
            {values.deliveryMethod === DeliveryMethod.Webhook && (
              <Button
                variant="ghost"
                size="md"
                onClick={() => handleTest(values)}
                disabled={
                  !values.callbackUrl ||
                  !values.requestTemplate ||
                  isTestingWebhook
                }
                loading={isTestingWebhook}
                startIcon={<Icons.PaperPlaneTilt />}
                testId="alert-test-webhook-button"
              >
                Test
              </Button>
            )}

            {/* Right side: Cancel + Save buttons */}
            <Stack direction="row" gap="16px">
              <WhenMode mode={['create', 'edit']}>
                <Button variant="secondary" size="md" onClick={onCancel}>
                  Cancel
                </Button>
                <Tooltip
                  title="Workspace is not connected"
                  wrapIf={
                    values.deliveryMethod === DeliveryMethod.Slack &&
                    slackIntegrationStatus !== 'connected'
                  }
                >
                  <span>
                    <Button
                      size="md"
                      type="submit"
                      disabled={
                        values.deliveryMethod === DeliveryMethod.Slack &&
                        slackIntegrationStatus !== 'connected'
                      }
                      testId="alert-save-button"
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
              </WhenMode>
            </Stack>
          </Stack>
        </Stack>
      )}
    </RffForm>
  );
};
